import React from "react";
import { FaCheck, FaLightbulb, FaRocket, FaUsers } from "react-icons/fa";
import { motion } from "framer-motion";
import "./DigitalBusinessStartup.css";

const packages = [
  {
    name: "Starter",
    price: "$199",
    features: [
      "Business Idea Consultation",
      "Website & Domain Setup",
      "Email & CRM Setup",
      "Basic Marketing Strategy",
      "1 Month Support",
    ],
    color: "#1E88E5",
  },
  {
    name: "Professional",
    price: "$399",
    features: [
      "Advanced Business Strategy",
      "Website + E-Commerce Setup",
      "CRM & Automation",
      "Social Media Marketing",
      "3 Months Support",
    ],
    color: "#43A047",
  },
  {
    name: "Enterprise",
    price: "$699",
    features: [
      "Complete Digital Business Launch",
      "Custom Website & Mobile App",
      "Full Marketing Automation",
      "SEO & Paid Ads Management",
      "6 Months Premium Support",
    ],
    color: "#E53935",
  },
];

const steps = [
  { icon: <FaLightbulb />, title: "Idea & Strategy", desc: "We analyze your business idea and create a roadmap." },
  { icon: <FaRocket />, title: "Implementation", desc: "Build your website, apps, and automation tools." },
  { icon: <FaUsers />, title: "Launch & Support", desc: "Launch your business online and provide support." },
];

const benefits = [
  {
    icon: <FaCheck />,
    title: "Expert Guidance",
    description: "Our team guides you at every step for a successful launch.",
  },
  {
    icon: <FaCheck />,
    title: "Cost Effective",
    description: "Affordable packages for startups with maximum ROI.",
  },
  {
    icon: <FaCheck />,
    title: "Scalable Solutions",
    description: "Future-proof solutions to grow your digital business easily.",
  },
];

const DigitalBusinessStartup = () => {
  return (
    <div className="digital-business-page">
      {/* HERO */}
      <section className="hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <motion.h1
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Launch Your <span className="highlight">Digital Business</span>
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We provide all the tools, strategies, and support to start and grow your online business.
          </motion.p>
          <motion.button
            className="cta-btn"
            whileHover={{ scale: 1.05, boxShadow: "0 0 15px rgba(255,255,255,0.7)" }}
          >
            Get Started
          </motion.button>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="section">
        <h2>Startup Packages</h2>
        <div className="package-grid">
          {packages.map((pkg, idx) => (
            <motion.div
              key={idx}
              className="package-card"
              whileHover={{ scale: 1.05, boxShadow: `0 0 20px ${pkg.color}` }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.2 }}
            >
              <h3 style={{ color: pkg.color }}>{pkg.name}</h3>
              <p className="price">{pkg.price}</p>
              <ul>
                {pkg.features.map((f, i) => (
                  <li key={i}>
                    <FaCheck className="check-icon" /> {f}
                  </li>
                ))}
              </ul>
              <button className="package-btn" style={{ backgroundColor: pkg.color }}>
                Choose Plan
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section alt-bg">
        <h2>How We Help You Launch</h2>
        <div className="steps">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              className="step-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.2 }}
            >
              <div className="step-icon">{step.icon}</div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section">
        <h2>Benefits of Our Startup Packages</h2>
        <div className="card-grid">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              className="benefit-card"
              whileHover={{ scale: 1.05, boxShadow: "0 15px 30px rgba(0,0,0,0.2)" }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2 }}
            >
              <div className="benefit-icon">{b.icon}</div>
              <h3>{b.title}</h3>
              <p>{b.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <h2>Ready to Launch Your Business?</h2>
        <p>Start today with expert guidance and affordable packages.</p>
        <motion.button
          className="cta-btn"
          whileHover={{ scale: 1.1, boxShadow: "0 0 25px rgba(255,255,255,0.8)" }}
        >
          Get Started
        </motion.button>
      </section>
    </div>
  );
};

export default DigitalBusinessStartup;
